import { ReactElement, CSSProperties } from "react";
import Chart from "./chart";
import { getCoordinatesForAngle, getMiddle, useChartDimensions, useUniqueId } from "./chartUtils";
import * as d3 from "d3";
import { IPieConfig, IPoint, ISeries } from "./types";
import { formatNumber } from "../../system/utils";
import { getPalette } from "../../configuration/configuration";
import HtmlTooltip from "./tooltip";
import { useTheme } from "@mui/material";
import Legend from "./legend";

interface IPieChartProps {
  pieConfig: IPieConfig;
  sx?: CSSProperties;
}

const PieChart = ({ pieConfig, sx }: IPieChartProps): ReactElement => {
  const theme = useTheme();
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: pieConfig.height ?? undefined,
    width: pieConfig.width ?? undefined,
    radius: Math.min(pieConfig.width ?? Number.MAX_VALUE, pieConfig.height ?? Number.MAX_VALUE) / 2.0,
    marginBottom: 10,
    marginLeft: 10,
    marginRight: 10,
    marginTop: pieConfig.showLegend ? 20 : 10
  });

  let pending = pieConfig.maximum ?? 0;
  pieConfig.series.forEach(x => pending -= x.value as number);

  const arcGenerator = d3.pie<ISeries>()
    .padAngle(pieConfig.padAngle)
    .value(d => d.value as number)
    .sort((a: ISeries, b: ISeries) => {
      if (!pieConfig.sort || pieConfig.sort === "VALUE"){
        if(a.name !== "" && b.name !== "") {
          return (b.value as number) - (a.value as number);
        }

        return a.name !== "" ? -1 : 0;
      }

      if (pieConfig.sort === "NAME"){
        if(a.name > b.name) {
          return 1;
        }

        return b.name > a.name ? -1 : 0;
      }

      return 0;
    });

  const arcs = arcGenerator(pending <= 0 ? pieConfig.series : [
    ...pieConfig.series.filter(x => (x.value as number) > 0),
    {
      value: pending,
      color: pieConfig.emptyColor ?? getPalette().statusColor.empty.background,
      name: "{@EMPTY}"
    }
  ]);
  const radius = dimensions.boundedRadius ?? (pieConfig.radius ? pieConfig.radius : 1.0) * Math.min(dimensions.boundedWidth, dimensions.boundedHeight) / 2 - 10;
  const arc = d3.arc()
    .innerRadius(radius * Math.max(pieConfig.padAngle, pieConfig.innerRadius)) // set to 0 for a pie chart
    .outerRadius(radius);

  const chartID = useUniqueId("pie-chart");

  let total = 0;
  pieConfig.series.forEach(x => total += x.value as number);

  return(
    <div ref={ref} style={{ ...sx, width: "100%", height: "calc(100% - 10px)" }} className="display-grid justify-content-center align-content-center" >
      <Chart dimensions={dimensions} >
        {pieConfig.showLegend && <Legend series={pieConfig.series} chartID={chartID} width={dimensions.boundedWidth} /> }

        <g>
          {pieConfig.title && <text className="Axis__label" textAnchor="middle"
            x={getMiddle(dimensions.boundedWidth)}
            y={dimensions.boundedHeight + 30} fill={theme.palette.text.primary} fontWeight="bold" fontSize="1em">
            {pieConfig.title}
          </text>}
        </g>

        <g>
          {pieConfig.showTotal &&
          <text x={dimensions.boundedWidth / 2} y={dimensions.boundedHeight / 2}
            fontWeight="bold"
            fontSize="0.9em"
            fill={theme.palette.text.primary}
            textAnchor="middle" dominantBaseline="middle">
            {formatNumber(total, 1)}
          </text>
          }
        </g>

        <g transform={`translate(${dimensions.boundedWidth / 2}, ${15 + dimensions.boundedHeight / 2})`}>
          {arcs.map((item, index) => {
            const path = arc(item as any) as string;

            if (item.data.name === "{@EMPTY}") {
              return <path  key={`${chartID}-path-${index}`} fill={item.data.color} d={path}/>;
            }

            const angle = (item.startAngle + item.endAngle) / 2.0;
            const [labelX1, labelY1] = getCoordinatesForAngle(angle, radius * (pieConfig.valueRadius ?? .45));
            const [labelX2, labelY2] = getCoordinatesForAngle(angle, radius * pieConfig.seriesRadius);
            // const textAnchor1 =  Math.abs(labelX1) < 5 ? "middle" : labelX1 > 0 ? "start"  : "end";
            const textAnchor2 =  Math.abs(labelX2) < 5 ? "middle" : labelX2 > 0 ? "start"  : "end";

            const seriesDetail = pieConfig.seriesDetail ? pieConfig.seriesDetail.find(d => d.name === item.data.name) : undefined;

            return (
              <g key={`${chartID}-path-${index}`}>
                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-row align-items-center" >
                      <div style={{ background: item.data.color, width: 10, height: 10, marginRight: 10 }} />
                      <strong className="flex-grow-1">{item.data.name}</strong>
                      <strong className="flex-grow-0">
                        {pieConfig.formatValue ? pieConfig.formatValue(item.data.value as number, index) : formatNumber(item.data.value as number, 1)}
                        {item.data.unit && <sub>{`[${item.data.unit}]`}</sub>}
                      </strong>
                    </div>

                    { seriesDetail && (seriesDetail.value as IPoint[]).map((d, di) => {

                      return(
                        <div className="display-flex-row align-items-center" key={`${chartID}-path-${index}-${di}`}>
                          <div style={{ width: 10, height: 10, marginRight: 10 }} />
                          <strong className="flex-grow-1">{d.x}</strong>
                          <strong className="flex-grow-0">
                            {pieConfig.formatValue ? pieConfig.formatValue(d.y as number, index) : formatNumber(d.y, 1)}
                            {item.data.unit && <sub>{`[${item.data.unit}]`}</sub>}
                          </strong>
                        </div>
                      );
                    })
                    }

                  </div>
                }>
                  <path fill={item.data.color} d={path}/>
                </HtmlTooltip>

                {!pieConfig.showLegend && item.data.name !== "" &&
                  <text x={labelX2} y={labelY2}
                    className="tick-label"
                    fontWeight="bold"
                    // fill={item.data.color}
                    textAnchor={textAnchor2} dominantBaseline="middle">{item.data.name}</text>
                }

                {pieConfig.showLegend &&
                  <text x={labelX2} y={labelY2}
                    fontWeight="bold"
                    fill={item.data.color}
                    textAnchor={textAnchor2} dominantBaseline="middle">
                    {pieConfig.formatValue ? pieConfig.formatValue(item.data.value as number, index) : formatNumber(item.data.value as number, 1)}
                  </text>
                }

                {pieConfig.showValue &&
                  <text x={labelX1} y={labelY1}
                    fontWeight="bold"
                    fontSize={`${pieConfig.scale ?? 1}em`}
                    fill="whitesmoke"
                    textAnchor="middle" dominantBaseline="middle">
                    {pieConfig.formatValue ? pieConfig.formatValue(item.data.value as number, index) : formatNumber(item.data.value as number, 1)}
                  </text>
                }

              </g>
            );
          })
          }
        </g>
      </Chart>
    </div>
  );
};

export default PieChart;
